/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import {
  Button,
  Accordion,
  Checkbox,
  OSelect,
  LoaderIndicator,
  ProgramItem,
  BreadCrumbs,
  Map,
} from '../../components'
import { useGetCategories, useGetPrograms } from '../../hooks'
import { IFilterItem, ISelectItem } from '../../interfaces'
import uniqBy from 'lodash/uniqBy'
import { ActionMeta } from 'react-select'
import { FilterInputType, Ages, SortOptions } from '../../constants/enums'
import isEmpty from 'lodash/isEmpty'
import { Mixpanel } from '../../helpers/mixpanel'

const getFilterId = (): string | undefined => {
  const path = window.location.pathname
  const value = path.split('/').pop()
  return value
}

const CATEGORIES_TO_EXCLUDE_AGES_SERVED = ['Food', 'Legal', 'Transit', 'More', 'Hotline']

export interface IFiltersPage extends React.HTMLProps<HTMLDivElement> {
  className?: string
}

const FiltersPage = function ({ className, ...props }: IFiltersPage) {
  const { t } = useTranslation()
  const [selectedFilters, setSelectedFilters] = React.useState<IFilterItem[]>([])
  const [filterAccordionOpen, setFilterAccordionOpen] = React.useState<boolean>(true)
  const [narrowSearchAccordionOpen, setNarrowSearchAccordionOpen] = React.useState<boolean>(false)
  const [sort, setSort] = React.useState<SortOptions>(SortOptions.WILDCARD)
  const [showMap, setShowMap] = React.useState<boolean>(false)

  const categoryId = getFilterId() as string

  const {
    checkboxFilters,
    genderFilters,
    communityFilters,
    regionFilters,
    category,
    filterCategoryId,
  } = useGetCategories({
    categoryId,
  })

  const {
    loading: loadingPrograms,
    error: errorPrograms,
    programs,
  } = useGetPrograms({
    filterCategoryId: filterCategoryId as number,
    filters: selectedFilters,
    sort,
  })

  const handleSelectAll = React.useCallback(
    (checkboxFilters: IFilterItem[]) => {
      setSelectedFilters(checkboxFilters)
      setFilterAccordionOpen(true)
    },
    [checkboxFilters, selectedFilters, filterAccordionOpen],
  )

  const handleChangeSelect = React.useCallback(
    (newValue: ISelectItem[] | ISelectItem, actionMeta: ActionMeta<any>, type: FilterInputType) => {
      const newFilters = Array.isArray(newValue) ? newValue : [newValue]

      let removedItems: ISelectItem[] = []
      if (actionMeta.action === 'remove-value') {
        removedItems.push(actionMeta.removedValue)
      }

      if (actionMeta.action === 'clear') {
        removedItems = actionMeta.removedValues as ISelectItem[]
      }

      setSelectedFilters(
        uniqBy(
          [
            ...selectedFilters,
            ...newFilters.map((item) => {
              return {
                id: item.value,
                description: item.label,
                type,
              } as IFilterItem
            }),
          ],
          'id',
        ).filter((item) => !removedItems.map((item) => item.value).includes(item.id)),
      )
    },
    [selectedFilters],
  )

  return (
    <div className={cx(className, 'ocla-filters-page')} {...props}>
      {loadingPrograms && <LoaderIndicator />}
      <section className='ocla-filters-page__top-section'>
        <Button
          onClick={() => {
            setSelectedFilters([])
            setFilterAccordionOpen(true)
          }}
          variant='secondary'
          title={t('filters.clearAll')}
        />
        <Button
          onClick={() => handleSelectAll(checkboxFilters || [])}
          variant='secondary'
          title={t('filters.selectAll')}
        />
        {/* <Button variant='primary' title={t('filters.search')} /> */}
      </section>
      <section className='ocla-filters-page__accordion-section'>
        <Accordion
          title={category?.name || ''}
          open={filterAccordionOpen}
          setIsOpen={() => setFilterAccordionOpen(!filterAccordionOpen)}
        >
          {checkboxFilters?.map((filterItem: IFilterItem) => (
            <Checkbox
              key={filterItem.id}
              name={filterItem.description}
              title={filterItem.description}
              info={filterItem.information}
              checked={selectedFilters.map((filter) => filter.id).includes(filterItem.id)}
              onChange={(e: any) => {
                let newSelectedFilter

                if (e.target.checked) {
                  newSelectedFilter = [...selectedFilters, filterItem]
                } else {
                  newSelectedFilter = selectedFilters.filter((item) => item.id !== filterItem.id)
                }

                setSelectedFilters(newSelectedFilter)
                const selectedDescriptions = newSelectedFilter.map((filter) => filter.description)

                if (selectedDescriptions.length > 0) {
                  Mixpanel.track('Search', {
                    Keyword: selectedDescriptions.join(', '),
                    Type: 'Category',
                    Parent: category?.name || '',
                  })
                }
              }}
            />
          ))}
        </Accordion>
        <Accordion
          title={t('filters.narrowYourSearch')}
          open={narrowSearchAccordionOpen}
          setIsOpen={() => setNarrowSearchAccordionOpen(!narrowSearchAccordionOpen)}
        >
          {!CATEGORIES_TO_EXCLUDE_AGES_SERVED.includes(category?.name || '') && (
            <OSelect
              title={t('filters.agesServed')}
              closeMenuOnSelect={false}
              isMulti
              // @ts-ignore
              onChange={(newValue: ISelectItem[] | ISelectItem, actionMeta: ActionMeta<any>) =>
                handleChangeSelect(newValue, actionMeta, FilterInputType.AGES)
              }
              options={[
                { value: Ages.UNDER_18, label: t('filters.under18') },
                { value: Ages.YOUTH_ONLY, label: t('filters.youthOnlyUp') },
                { value: Ages.ADULT_ONLY, label: t('filters.adultsOnlyUp') },
                { value: Ages.SENIOR_ONLY, label: t('filters.seniorsOnlyUp') },
              ]}
            />
          )}
          {!isEmpty(communityFilters) && (
            <OSelect
              title={t('filters.community')}
              closeMenuOnSelect={false}
              isMulti
              // @ts-ignore
              onChange={(newValue: ISelectItem[] | ISelectItem, actionMeta: ActionMeta<any>) =>
                handleChangeSelect(newValue, actionMeta, FilterInputType.DYNAMIC_MULTI_SELECT)
              }
              options={communityFilters?.map((filterItem: IFilterItem) => {
                return { value: filterItem.id, label: filterItem.description }
              })}
            />
          )}
          {!isEmpty(genderFilters) && (
            <OSelect
              title={t('filters.gender')}
              closeMenuOnSelect={false}
              isMulti
              // @ts-ignore
              onChange={(newValue: ISelectItem[] | ISelectItem, actionMeta: ActionMeta<any>) =>
                handleChangeSelect(newValue, actionMeta, FilterInputType.DYNAMIC_MULTI_SELECT)
              }
              options={genderFilters?.map((filterItem: IFilterItem) => {
                return { value: filterItem.id, label: filterItem.description }
              })}
            />
          )}
          {!isEmpty(regionFilters) && (
            <OSelect
              title={t('filters.neigborhood')}
              closeMenuOnSelect={false}
              isMulti
              // @ts-ignore
              onChange={(newValue: ISelectItem[] | ISelectItem, actionMeta: ActionMeta<any>) =>
                handleChangeSelect(newValue, actionMeta, FilterInputType.NEIGHBORHOOD)
              }
              options={regionFilters?.map((filterItem: IFilterItem) => {
                return { value: filterItem.id, label: filterItem.description }
              })}
            />
          )}
        </Accordion>
      </section>
      <section className='ocla-filters-page__bottom-search-section'>
        {/* <Button variant='primary' title={t('filters.search')} /> */}
        <div className='ocla-filters-page__bottom-search-section__title'>
          {!isEmpty(programs) && t('filters.totalResults', { count: programs?.length })}
        </div>
        <BreadCrumbs
          selectedOption={showMap ? 'map' : 'list'}
          sortValue={sort}
          onChangeSort={(value: SortOptions) => {
            setSort(value)
          }}
          onClickList={() => {
            setShowMap(false)
          }}
          onClickMap={() => {
            setShowMap(true)
          }}
        />
      </section>
      <section className='ocla-filters-page__bottom-section'>
        {showMap ? (
          <Map programs={programs} />
        ) : (
          programs?.map(
            (program, index) =>
              program && <ProgramItem key={`${program.id}=${index}`} program={program} />,
          )
        )}
      </section>
    </div>
  )
}

export default FiltersPage
